import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faPhoneVolume, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';

import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
// Generischer HTTP-Wrapper für Endpunkte, die der erzeugte Client noch nicht kennt.
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';
import { apiErrorMessage } from 'Pages/Calendar/errorText';
import { LabelEmployee, LabelTask } from 'Pages/Tasks/labelTaskTypes';

/*
 * Lokal statt aus dem erzeugten Client: `src/apiClient/Api.ts` entsteht aus dem Swagger eines
 * LAUFENDEN Servers (`npm run generate-api`) und kennt die Callback-Endpunkte noch nicht.
 */
interface CallbackDto {
    id: string;
    name: string;
    phone: string;
    summary: string;
    createdBy: string | null;
    createdAt: string;
    claimedBy: string | null;
    claimedAt: string | null;
    completed: boolean;
    completedBy: string | null;
    completedAt: string | null;
    resolutionNote: string | null;
    youTrackIssueId: string | null;
    labelTaskId: number | null;
    convertedBy: string | null;
    convertedAt: string | null;
}

interface CreateCallbackResponse {
    callback: CallbackDto;
    teamsNotified: boolean;
}

interface ConvertCallbackResponse {
    callback: CallbackDto;
    task: LabelTask;
}

const emptyDraft = { name: '', phone: '', summary: '' };

const when = (iso: string): string => format(parseISO(iso), 'dd.MM.yyyy HH:mm');

/**
 * Rückrufbitten: ein Mitarbeiter nimmt einen Anruf an und legt die Bitte an, das Team wird im
 * Teams-Kanal benachrichtigt, jemand übernimmt den Rückruf („ich mach das") und schließt ihn ab
 * — mit optionaler Notiz, was dabei herauskam.
 *
 * Bewusst systemintern statt als YouTrack-Ticket: die meisten Rückrufe sind nach zwei Minuten
 * erledigt und verdienen kein Helpdesk-Ticket. Anlegen, Übernehmen und Abschließen sind einzeln
 * über das Permission-System vergeben — die Liste sehen alle internen Rollen.
 */
export const CallbackRequests: React.FC = () => {
    const context = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    // Über request() statt api.instance — sonst fehlt der Authorization-Header.
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const canCreate = context.hasPermission('callbacks.create');
    const canClaim = context.hasPermission('callbacks.claim');
    const canComplete = context.hasPermission('callbacks.complete');
    const canConvert = context.hasPermission('callbacks.convert');

    const [callbacks, setCallbacks] = useState<CallbackDto[]>([]);
    const [includeCompleted, setIncludeCompleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [draft, setDraft] = useState(emptyDraft);

    // Die Bitte, deren Erledigen-Dialog gerade offen ist, samt Notiz-Entwurf.
    const [completing, setCompleting] = useState<CallbackDto | null>(null);
    const [note, setNote] = useState('');

    // Die Bitte, deren Überführungs-Dialog gerade offen ist, samt Formular.
    const [converting, setConverting] = useState<CallbackDto | null>(null);
    const [assignee, setAssignee] = useState<LabelEmployee | null>(null);
    const [convSubject, setConvSubject] = useState('');
    const [convText, setConvText] = useState('');
    const [convDueDate, setConvDueDate] = useState('');
    const [convComplete, setConvComplete] = useState(false);
    const [employees, setEmployees] = useState<LabelEmployee[]>([]);

    // Mitarbeiterliste für die Zuweisung — nur wer überführen darf, braucht sie.
    useEffect(() => {
        if (!canConvert) return;
        http.get<LabelEmployee[]>('/Tasks/Employees')
            .then((r) => setEmployees(r.data))
            // Still: ohne Liste bleibt die Auswahl leer, der Rest der Seite funktioniert weiter.
            .catch(() => setEmployees([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canConvert]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await http.get<CallbackDto[]>('/Callback', { includeCompleted });
            setCallbacks(r.data);
        } catch (e) {
            context.apiError(e);
        } finally {
            setLoading(false);
        }
    }, [context, http, includeCompleted]);

    useEffect(() => {
        void load();
    }, [load]);

    const create = async (): Promise<void> => {
        setBusy(true);
        try {
            const r = await http.post<CreateCallbackResponse>('/Callback', undefined, draft);
            setCreateOpen(false);
            setDraft(emptyDraft);
            if (r.data.teamsNotified) {
                enqueueSnackbar('Rückrufbitte angelegt — das Team ist benachrichtigt.', { variant: 'success' });
            } else {
                enqueueSnackbar(
                    'Rückrufbitte angelegt — aber keine Teams-Benachrichtigung (Webhook fehlt oder fehlgeschlagen).',
                    { variant: 'warning' });
            }
            await load();
        } catch (e: any) {
            // Der Server antwortet bei Validierungsfehlern mit Klartext.
            const detail = e?.response?.status === 400 ? e?.response?.data : null;
            if (typeof detail === 'string' && detail.length > 0) {
                enqueueSnackbar(detail, { variant: 'error' });
            } else {
                context.apiError(e);
            }
        } finally {
            setBusy(false);
        }
    };

    const complete = async (): Promise<void> => {
        if (!completing) return;
        setBusy(true);
        try {
            await http.post<CallbackDto>(
                `/Callback/${completing.id}/complete`, undefined, { resolutionNote: note.trim() || null });
            setCompleting(null);
            setNote('');
            enqueueSnackbar('Rückruf erledigt.', { variant: 'success' });
            await load();
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    const claim = async (callback: CallbackDto): Promise<void> => {
        setBusy(true);
        try {
            await http.post<CallbackDto>(`/Callback/${callback.id}/claim`);
            enqueueSnackbar('Übernommen — der Rückruf gehört dir.', { variant: 'success' });
            await load();
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    /** Öffnet den Überführungs-Dialog mit Vorbelegung aus der Bitte. */
    const startConvert = (callback: CallbackDto): void => {
        setConverting(callback);
        setAssignee(null);
        setConvSubject(`Rückruf ${callback.name} (${callback.phone})`);
        setConvText(callback.summary);
        setConvDueDate('');
        setConvComplete(false);
    };

    const convert = async (): Promise<void> => {
        if (!converting || !assignee) return;
        setBusy(true);
        try {
            const r = await http.post<ConvertCallbackResponse>(
                `/Callback/${converting.id}/convert-task`, undefined, {
                    employeeId: assignee.employeeId,
                    subject: convSubject.trim() || null,
                    text: convText.trim() || null,
                    dueDate: convDueDate || null,
                    completeCallback: convComplete,
                });
            setConverting(null);
            enqueueSnackbar(
                `Überführt in Labelwin-Aufgabe #${r.data.task.id} für ${assignee.name ?? assignee.employeeId}.`,
                { variant: 'success' });
            await load();
        } catch (e) {
            // 400/403/409/503 tragen die Begründung des Servers (etwa: Schreibzugriff
            // abgeschaltet, bereits überführt) in Klartext.
            enqueueSnackbar(apiErrorMessage(e, 'Überführung fehlgeschlagen.'), { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const reopen = async (callback: CallbackDto): Promise<void> => {
        setBusy(true);
        try {
            await http.post<CallbackDto>(`/Callback/${callback.id}/reopen`);
            await load();
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    const open = callbacks.filter((c) => !c.completed);
    const done = callbacks.filter((c) => c.completed);

    const card = (callback: CallbackDto): React.ReactNode => (
        <Paper key={callback.id} sx={{ p: 2, opacity: callback.completed ? 0.75 : 1 }}>
            <Stack direction="row" spacing={2}
                   sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}>
                <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{callback.name}</Typography>
                        <Link href={`tel:${callback.phone.replace(/[^+0-9]/g, '')}`} underline="hover">
                            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                <FontAwesomeIcon icon={faPhoneVolume}/>
                                <span>{callback.phone}</span>
                            </Stack>
                        </Link>
                        {callback.youTrackIssueId && (
                            <Chip size="small" variant="outlined" label={callback.youTrackIssueId}/>
                        )}
                        {callback.labelTaskId && (
                            <Chip size="small" color="success" variant="outlined"
                                  label={`Labelwin-Aufgabe #${callback.labelTaskId}`}/>
                        )}
                        {!callback.completed && callback.claimedBy && (
                            <Chip size="small" color="info" variant="outlined"
                                  label={`Übernommen: ${callback.claimedBy}`}/>
                        )}
                    </Stack>
                    {callback.summary && (
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {callback.summary}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Angenommen von {callback.createdBy ?? 'unbekannt'} · {when(callback.createdAt)}
                        {callback.claimedBy && callback.claimedAt &&
                            ` — übernommen von ${callback.claimedBy} · ${when(callback.claimedAt)}`}
                        {callback.completed && callback.completedAt &&
                            ` — abgeschlossen von ${callback.completedBy ?? 'unbekannt'} · ${when(callback.completedAt)}`}
                    </Typography>
                    {callback.completed && callback.resolutionNote && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            {callback.resolutionNote}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    {callback.completed && canComplete && (
                        <Button size="small" variant="text" disabled={busy}
                                onClick={() => void reopen(callback)}>Wieder öffnen</Button>
                    )}
                    {!callback.completed && canConvert && !callback.labelTaskId && (
                        <Button size="small" variant="text" disabled={busy}
                                onClick={() => startConvert(callback)}>
                            In Aufgabe überführen
                        </Button>
                    )}
                    {!callback.completed && canClaim && !callback.claimedBy && (
                        <Button size="small" variant="contained" disabled={busy}
                                onClick={() => void claim(callback)}>
                            Ich mach das
                        </Button>
                    )}
                    {!callback.completed && canClaim && callback.claimedBy && (
                        <Button size="small" variant="text" disabled={busy}
                                onClick={() => void claim(callback)}>
                            Übernehmen
                        </Button>
                    )}
                    {!callback.completed && canComplete && (
                        <Button size="small" variant={callback.claimedBy ? 'contained' : 'outlined'}
                                disabled={busy}
                                onClick={() => { setCompleting(callback); setNote(''); }}>
                            Abschließen
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );

    return (
        <Grid size={12}>
            <Title>Rückrufe</Title>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
                {canCreate && (
                    <Button variant="contained" startIcon={<FontAwesomeIcon icon={faPlus}/>}
                            onClick={() => setCreateOpen(true)}>
                        Neue Rückrufbitte
                    </Button>
                )}
                <Button variant="text" startIcon={<FontAwesomeIcon icon={faArrowsRotate}/>}
                        onClick={() => void load()} disabled={loading}>
                    Aktualisieren
                </Button>
                <Box sx={{ flexGrow: 1 }}/>
                <FormControlLabel
                    control={<Switch checked={includeCompleted}
                                     onChange={(e) => setIncludeCompleted(e.target.checked)}/>}
                    label="Erledigte anzeigen"/>
            </Stack>

            {loading && <LinearProgress sx={{ mb: 2 }}/>}

            {!loading && open.length === 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>Keine offenen Rückrufbitten.</Alert>
            )}

            <Stack spacing={1.5}>
                {open.map(card)}
                {includeCompleted && done.length > 0 && (
                    <>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                            Erledigt
                        </Typography>
                        {done.map(card)}
                    </>
                )}
            </Stack>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Neue Rückrufbitte</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField autoFocus fullWidth size="small" label="Name"
                                   value={draft.name}
                                   onChange={(e) => setDraft({ ...draft, name: e.target.value })}/>
                        <TextField fullWidth size="small" label="Rufnummer"
                                   value={draft.phone}
                                   onChange={(e) => setDraft({ ...draft, phone: e.target.value })}/>
                        <TextField fullWidth size="small" label="Kurze Zusammenfassung"
                                   multiline minRows={3}
                                   value={draft.summary}
                                   onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                                   helperText="Worum geht es? Ein, zwei Sätze reichen."/>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
                    <Button variant="contained" onClick={() => void create()}
                            disabled={busy || !draft.name.trim() || !draft.phone.trim()}>
                        Anlegen
                    </Button>
                </DialogActions>
            </Dialog>

            {/*
              * Überführung in eine Labelwin-Aufgabe: der Zuständige wird ausdrücklich gewählt,
              * Betreff und Text sind aus der Bitte vorbelegt. Der Server weist eine zweite
              * Überführung mit 409 ab — der Knopf verschwindet nach der ersten ohnehin.
              */}
            <Dialog open={converting !== null} onClose={() => setConverting(null)} fullWidth maxWidth="sm">
                <DialogTitle>In Labelwin-Aufgabe überführen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            {converting?.name} · {converting?.phone}
                        </Typography>
                        <Autocomplete
                            size="small"
                            options={employees}
                            value={assignee}
                            onChange={(_, value) => setAssignee(value)}
                            getOptionLabel={(option) => option.name ?? option.labelUser ?? String(option.employeeId)}
                            isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                            renderInput={(params) => (
                                <TextField {...params} autoFocus label="Zuständiger Mitarbeiter"
                                           helperText="Wer soll sich in Labelwin darum kümmern?"/>
                            )}
                        />
                        <TextField fullWidth size="small" label="Betreff"
                                   value={convSubject}
                                   slotProps={{ htmlInput: { maxLength: 100 } }}
                                   onChange={(e) => setConvSubject(e.target.value)}/>
                        <TextField fullWidth size="small" label="Text" multiline minRows={3}
                                   value={convText}
                                   onChange={(e) => setConvText(e.target.value)}/>
                        <TextField fullWidth size="small" type="date" label="Fällig am (optional)"
                                   value={convDueDate}
                                   slotProps={{ inputLabel: { shrink: true } }}
                                   onChange={(e) => setConvDueDate(e.target.value)}/>
                        <FormControlLabel
                            control={<Checkbox checked={convComplete}
                                               onChange={(e) => setConvComplete(e.target.checked)}/>}
                            label="Rückrufbitte gleich abschließen"/>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConverting(null)} disabled={busy}>Abbrechen</Button>
                    <Button variant="contained" onClick={() => void convert()}
                            disabled={busy || !assignee}>
                        Überführen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={completing !== null} onClose={() => setCompleting(null)} fullWidth maxWidth="sm">
                <DialogTitle>Rückrufbitte abschließen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            {completing?.name} · {completing?.phone}
                        </Typography>
                        <TextField autoFocus fullWidth size="small" label="Notiz (optional)"
                                   multiline minRows={2}
                                   value={note}
                                   onChange={(e) => setNote(e.target.value)}
                                   helperText="Was kam beim Rückruf heraus? Leer lassen ist in Ordnung."/>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleting(null)}>Abbrechen</Button>
                    <Button variant="contained" onClick={() => void complete()} disabled={busy}>
                        Erledigt
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default CallbackRequests;
